// « Réservation manuelle » — the phone booking's model (PB5, PMS-PHONE-BOOKING-BRIEF §2).
//
// Pure: the call as the desk fills it in, and what it becomes — the desk's
// offer query (PB3), the manual command (PB4), an anonymous enquiry (PB9), the
// read-back and the confirmation to copy. The screen (`CallFlow.tsx`) holds a
// `CallDraft` and asks this module everything else, so a scenario can be
// replayed without rendering it.

import {
  ARRHES_METHODS,
  CARD_LINK_MARK,
  DEFAULT_TRANSFER_DUE_DAYS,
  renderTerms,
  termsDate,
  transferDueOn as defaultTransferDue,
  transferDueProblem,
  transferInstructionLines,
  type AgeBand,
  type ArrhesMethod,
  type BankTransferDetails,
  type ConfirmationMailLang,
  type BedConfig,
  type Cents,
  type DeskSale,
  type DeskStayQuery,
  type GuestLang,
  type IsoDate,
  type Product,
  type RatePlan,
  type ReservationCommand,
  type Room,
  type RoomStayView,
  type RoomType,
  type StayPersonInput,
  type StoredCancellationPolicy,
  type TermsRules,
  type TurnawayInput,
} from '@lemontaigu/pms-core';
import { fmtAmount, fmtEuros } from '../logic.js';

// ── The steps (§2) ───────────────────────────────────────────────────────────

export type CallLane = 'new' | 'known';
export type CallStep = 'accueil' | 'client' | 'dates' | 'offre' | 'decision' | 'coord' | 'details' | 'arrhes' | 'recap';

export const STEP_LABEL: Record<CallStep, string> = {
  accueil: 'Accueil',
  client: 'Fiche client',
  dates: 'Dates et voyageurs',
  offre: 'Offre',
  decision: 'Réserver ?',
  coord: 'Coordonnées',
  details: 'Détails',
  arrhes: 'Arrhes',
  recap: 'Récapitulatif',
};

/** The steps after the fork open on « oui » only (§2). */
export const AFTER_YES: ReadonlySet<CallStep> = new Set(['coord', 'details', 'arrhes', 'recap']);

export function laneSteps(lane: CallLane | null): CallStep[] {
  const tail: CallStep[] = ['dates', 'offre', 'decision', 'coord', 'details', 'arrhes', 'recap'];
  return lane === 'known' ? ['accueil', 'client', ...tail] : ['accueil', ...tail];
}

/** The opening line, with the name of whoever answers — so the caller, and the
 *  journal, know who took the call (operator, 2026-10-27). Nobody identified
 *  yet: the placeholder. */
export const greetingFr = (firstName: string | null | undefined) =>
  `« Le Montaigu, Bonjour, ${firstName?.trim() || '[votre prénom]'}, je vous écoute »`;

/** The button that opens the flow for an établissement: « Résa Hôtel », « Résa
 *  Appt » (operator, 2026-10-27; « Appel — … » before). */
export const callButtonLabel = (p: { kind: 'hotel' | 'apartments' }) => (p.kind === 'apartments' ? 'Résa Appt' : 'Résa Hôtel');

// ── The draft ────────────────────────────────────────────────────────────────

export type DepositChoice = { kind: 'pct'; pct: number } | { kind: 'amount'; cents: Cents };

/**
 * One room of a booking (operator, 2026-10-27: several rooms in one call). The
 * draft's own party, pick, bed, room and price are the room being worked on;
 * `CallDraft.rooms` keeps every room, the one being worked on included (its
 * entry is refreshed whenever the desk moves to another room). One room, the
 * usual call: `rooms` stays empty and nothing below changes.
 */
export interface CallRoom {
  adults: number;
  childAges: (number | null)[];
  pick: { category: string; plan: string } | null;
  bed: string | null;
  roomId: number | null;
  overrideCents: Cents | null;
  /** The option as it was priced when the desk left this room — its own party's. */
  sale: SoldSale | null;
}

export interface CallDraft {
  propertyCode: string;
  lane: CallLane | null;
  lang: GuestLang;
  /** A returning guest's fiche, picked in « Fiche client » or at « Coordonnées ». */
  guestId: string | null;
  arrival: IsoDate | '';
  nights: number;
  adults: number;
  /** One entry per child, in years; null until the caller says (P5: mandatory before saving). */
  childAges: (number | null)[];
  /** The option kept: category SOLD and plan (codes). */
  pick: { category: string; plan: string } | null;
  /** The bed configuration asked for (`pms_bed_config.code`), among those the category's rooms take. */
  bed: string | null;
  /** The room given at the call (`pms_room.id`), assigned once the booking is
   *  saved — the journal's « Chambre attribuée » dates it. null = at the planning. */
  roomId: number | null;
  /** A negotiated price for the whole stay (P1: a manual price), in cents. */
  overrideCents: Cents | null;
  decision: 'book' | 'info' | null;
  lastName: string;
  firstName: string;
  phone: string;
  email: string;
  occupantName: string;
  arrivalFrom: string;
  arrivalTo: string;
  /** Catalogue product id → quantity. */
  extras: Record<number, number>;
  occasion: string;
  needs: string;
  note: string;
  deposit: DepositChoice | null;
  /** How the arrhes are paid (PB10): the secure card link, or a bank transfer to the property's account. */
  arrhes: ArrhesMethod;
  /** The cancellation policy the desk picked for this booking (`pms_cancellation_policy.code`), or null for
   *  the one the rate plan follows. */
  policyCode: string | null;
  /** With a transfer: the day it is due. '' = the default — the property's delay from today, never past the arrival. */
  transferDueOn: IsoDate | '';
  /** The terms relaxed for this guest (P11), or null for the plan's (or the picked policy's). */
  relaxed: TermsRules | null;
  confirmed: boolean;
  /** Every room of a multi-room booking; empty for one room. */
  rooms: CallRoom[];
  /** Which of `rooms` the draft's own fields are. */
  active: number;
}

export function emptyCall(propertyCode: string): CallDraft {
  return {
    propertyCode,
    lane: null,
    lang: 'fr',
    guestId: null,
    arrival: '',
    nights: 1,
    adults: 2,
    childAges: [],
    pick: null,
    bed: null,
    roomId: null,
    overrideCents: null,
    decision: null,
    lastName: '',
    firstName: '',
    phone: '',
    email: '',
    occupantName: '',
    arrivalFrom: '',
    arrivalTo: '',
    extras: {},
    occasion: '',
    needs: '',
    note: '',
    deposit: null,
    arrhes: 'vad',
    policyCode: null,
    transferDueOn: '',
    relaxed: null,
    confirmed: false,
    rooms: [],
    active: 0,
  };
}

// ── Several rooms ────────────────────────────────────────────────────────────

/** The room being worked on, as it stands. */
export const currentRoom = (c: CallDraft, sale: SoldSale | null): CallRoom => ({
  adults: c.adults,
  childAges: c.childAges,
  pick: c.pick,
  bed: c.bed,
  roomId: c.roomId,
  overrideCents: c.overrideCents,
  sale: c.pick ? sale : null,
});

/** Every room of the booking, the one being worked on up to date. */
export function allRooms(c: CallDraft, sale: SoldSale | null): CallRoom[] {
  if (c.rooms.length === 0) return [currentRoom(c, sale)];
  return c.rooms.map((r, i) => (i === c.active ? currentRoom(c, sale) : r));
}

const load = (r: CallRoom): Partial<CallDraft> => ({
  adults: r.adults,
  childAges: r.childAges,
  pick: r.pick,
  bed: r.bed,
  roomId: r.roomId,
  overrideCents: r.overrideCents,
});

/** « + Une autre chambre »: same dates, a new party, nothing picked yet. */
export function addRoom(c: CallDraft, sale: SoldSale | null): CallDraft {
  const rooms = allRooms(c, sale);
  const fresh: CallRoom = { adults: 2, childAges: [], pick: null, bed: null, roomId: null, overrideCents: null, sale: null };
  return { ...c, ...load(fresh), rooms: [...rooms, fresh], active: rooms.length, confirmed: false };
}

/** Back to room `i`: the room left is kept as it stands. */
export function switchRoom(c: CallDraft, sale: SoldSale | null, i: number): CallDraft {
  const rooms = allRooms(c, sale);
  const r = rooms[i];
  if (r === undefined || (i === c.active && c.rooms.length > 0)) return c;
  return { ...c, ...load(r), rooms, active: i };
}

/** « Retirer »: the last room left makes it a one-room call again. */
export function removeRoom(c: CallDraft, sale: SoldSale | null, i: number): CallDraft {
  const rooms = allRooms(c, sale).filter((_, j) => j !== i);
  if (rooms.length === 0) return c;
  const active = i < c.active ? c.active - 1 : i === c.active ? 0 : c.active;
  return { ...c, ...load(rooms[active]!), rooms: rooms.length === 1 ? [] : rooms, active: rooms.length === 1 ? 0 : active, confirmed: false };
}

/** Rooms of the host category the OTHER rooms already hold: what is left for this one. */
export function heldElsewhere(c: CallDraft, host: string): number {
  return c.rooms.filter((r, i) => i !== c.active && r.sale?.host === host).length;
}

// ── Dates and the party ──────────────────────────────────────────────────────

export function addDays(d: IsoDate, n: number): IsoDate {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
}

export const departureOf = (c: CallDraft): IsoDate | null => (c.arrival === '' ? null : addDays(c.arrival, c.nights));

/** The PMS band of a child's age (`pms_stay_person.age_band`); unknown until said. */
export function bandForAge(age: number | null): AgeBand {
  if (age === null) return 'child_unknown';
  if (age < 3) return 'infant';
  if (age <= 5) return 'child_3_5';
  if (age <= 8) return 'child_6_8';
  if (age <= 12) return 'child_9_12';
  return 'child_13_17';
}

/** The desk's offer query: an infant never counts for the room or the meals (D1). */
export function offerQuery(c: CallDraft): DeskStayQuery | null {
  const to = departureOf(c);
  if (to === null) return null;
  const bands = c.childAges.map(bandForAge);
  const children = bands.filter((b) => b !== 'infant');
  return {
    propertyCode: c.propertyCode,
    from: c.arrival as IsoDate,
    to,
    adults: c.adults,
    children: children.length,
    infants: bands.length - children.length,
    childBands: children,
  };
}

export const agesMissing = (c: Pick<CallDraft, 'childAges'>): boolean => c.childAges.some((a) => a === null);

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

type Party = Pick<CallDraft, 'adults' | 'childAges'>;

/** The whole booking's party, every room together. */
export function bookingParty(c: CallDraft): Party {
  if (c.rooms.length === 0) return c;
  const rooms = c.rooms.map((r, i) => (i === c.active ? { adults: c.adults, childAges: c.childAges } : r));
  return { adults: rooms.reduce((s, r) => s + r.adults, 0), childAges: rooms.flatMap((r) => r.childAges) };
}

export function partyFr(c: Party): string {
  const babies = c.childAges.filter((a) => a !== null && a < 3).length;
  const kids = c.childAges.length - babies;
  return [plural(c.adults, 'adulte', 'adultes'), kids ? plural(kids, 'enfant', 'enfants') : '', babies ? plural(babies, 'bébé', 'bébés') : '']
    .filter(Boolean)
    .join(', ');
}

export function partyEn(c: Party): string {
  const babies = c.childAges.filter((a) => a !== null && a < 3).length;
  const kids = c.childAges.length - babies;
  return [plural(c.adults, 'adult', 'adults'), kids ? plural(kids, 'child', 'children') : '', babies ? plural(babies, 'baby', 'babies') : '']
    .filter(Boolean)
    .join(', ');
}

const DOW = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const DOW_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const parts = (d: IsoDate) => {
  const t = new Date(`${d}T00:00:00Z`);
  return { dow: t.getUTCDay(), day: t.getUTCDate(), month: t.getUTCMonth() };
};

/** « vendredi 4 décembre » — the read-back says the weekday (§2). */
export function longFr(d: IsoDate): string {
  const p = parts(d);
  return `${DOW[p.dow]} ${p.day === 1 ? '1er' : p.day} ${MONTHS[p.month]}`;
}

export function longEn(d: IsoDate): string {
  const p = parts(d);
  return `${DOW_EN[p.dow]} ${p.day} ${MONTHS_EN[p.month]}`;
}

export function datesReadback(c: CallDraft): string {
  const to = departureOf(c);
  if (to === null) return '…';
  return `du ${longFr(c.arrival as IsoDate)} au ${longFr(to)}, soit ${plural(c.nights, 'nuit', 'nuits')}`;
}

// ── Spelling (« M comme Marcel ») ────────────────────────────────────────────

const PHONETIC: Record<string, string> = {
  A: 'Anatole', B: 'Berthe', C: 'Célestin', D: 'Désiré', E: 'Eugène', F: 'François', G: 'Gaston', H: 'Henri',
  I: 'Irma', J: 'Joseph', K: 'Kléber', L: 'Louis', M: 'Marcel', N: 'Nicolas', O: 'Oscar', P: 'Pierre',
  Q: 'Quintal', R: 'Raoul', S: 'Suzanne', T: 'Thérèse', U: 'Ursule', V: 'Victor', W: 'William', X: 'Xavier',
  Y: 'Yvonne', Z: 'Zoé',
};
const MARKS: Record<string, string> = {
  É: 'E accent aigu', È: 'E accent grave', Ê: 'E circonflexe', Ë: 'E tréma', À: 'A accent grave', Â: 'A circonflexe',
  Ç: 'C cédille', Î: 'I circonflexe', Ï: 'I tréma', Ô: 'O circonflexe', Ù: 'U accent grave', Û: 'U circonflexe',
  Ü: 'U tréma', '-': 'trait d’union', '’': 'apostrophe', "'": 'apostrophe', ' ': 'espace',
};

export function spellOut(name: string): { ch: string; word: string }[] {
  return [...name.trim().toUpperCase()].map((ch) => ({ ch, word: PHONETIC[ch] ?? MARKS[ch] ?? '' }));
}

/** « marie point dupont arobase exemple point fr » — the e-mail read back. */
export function speakEmail(e: string): string {
  return [...e.trim().toLowerCase()]
    .map((ch) => (ch === '.' ? ' point ' : ch === '@' ? ' arobase ' : ch === '-' ? ' tiret ' : ch === '_' ? ' tiret bas ' : ch))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

// ── Money: the stay, the extras, the deposit ─────────────────────────────────

export type SoldSale = Extract<DeskSale, { sellable: true }>;

/** The stay's room nights: the quote's, or the negotiated price spread over them
 *  (the remainder on the last night, so the nights sum to it exactly). */
export function nightPrices(sale: SoldSale, overrideCents: Cents | null): { date: IsoDate; priceCents: Cents }[] {
  if (overrideCents === null) return sale.quote.nights;
  const n = sale.quote.nights.length;
  const each = Math.floor(overrideCents / n);
  return sale.quote.nights.map((x, i) => ({ date: x.date, priceCents: i === n - 1 ? overrideCents - each * (n - 1) : each }));
}

export const stayTotal = (sale: SoldSale, overrideCents: Cents | null): Cents =>
  nightPrices(sale, overrideCents).reduce((s, x) => s + x.priceCents, 0);

/** The extras the desk may add: the catalogue's own, not taxes, not board meals. */
export const extraProducts = (products: readonly Product[]): Product[] =>
  products.filter((p) => p.active && !p.isTax && !p.autoApply && !p.boardPrestation && !p.isPassthrough);

export function extrasTotal(c: CallDraft, products: readonly Product[], persons: number): Cents {
  return Object.entries(c.extras).reduce((s, [id, qty]) => {
    const p = products.find((x) => x.id === Number(id));
    return p ? s + p.priceCents * qty * (p.perPerson ? persons : 1) : s;
  }, 0);
}

/** The terms this guest is sold under: relaxed, or the plan's (P11). */
export const termsFor = (c: CallDraft, planTerms: TermsRules | null): TermsRules | null => c.relaxed ?? planTerms;

/** The policies the desk may pick from: a policy the operator set, not an OTA extranet's mirror (PB10). */
export const deskPolicies = (policies: readonly StoredCancellationPolicy[]): StoredCancellationPolicy[] => policies.filter((p) => !p.ota);

/** The policy the desk picked, or null when it follows the plan's. */
export const pickedPolicy = (c: CallDraft, policies: readonly StoredCancellationPolicy[]): StoredCancellationPolicy | null =>
  c.policyCode === null ? null : (deskPolicies(policies).find((p) => p.code === c.policyCode) ?? null);

/** A policy's rules alone — what a booking copies. */
export const policyRules = (p: StoredCancellationPolicy): TermsRules => ({
  prepaymentPct: p.prepaymentPct,
  prepaymentAt: p.prepaymentAt,
  tiers: p.tiers.map((t) => ({ moreThanDaysBefore: t.moreThanDaysBefore, retainPct: t.retainPct })),
  noShowPct: p.noShowPct,
});

/** The day a transfer is due: the desk's, else the property's delay from today, never past the arrival. */
export function transferDue(c: CallDraft, bank: Pick<BankTransferDetails, 'dueDays'> | null, today: IsoDate): IsoDate | null {
  if (c.arrival === '') return null;
  if (c.transferDueOn !== '') return c.transferDueOn;
  return defaultTransferDue(today, c.arrival, bank?.dueDays ?? DEFAULT_TRANSFER_DUE_DAYS);
}

export { ARRHES_METHODS };

/** The default deposit: none (operator, 2026-10-27 — « 0 % arrhes by default »);
 *  the desk raises it when the caller agrees. The terms' prepayment is shown
 *  beside it, never applied on its own. */
export const DEFAULT_DEPOSIT: DepositChoice = { kind: 'pct', pct: 0 };

/** On the room nights (board in, extras and taxe de séjour out — the A5 base). */
export function depositCents(choice: DepositChoice, roomNights: Cents): Cents {
  return choice.kind === 'amount' ? choice.cents : Math.round((roomNights * choice.pct) / 100);
}

// ── What a booking needs (« Pour réserver ») ─────────────────────────────────

const EMAIL = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,}$/;
export const phoneOk = (p: string) => /^[+0-9 ().-]{6,30}$/.test(p.trim());
export const emailOk = (e: string) => EMAIL.test(e.trim());

export interface CheckItem {
  label: string;
  ok: boolean;
  step: CallStep;
  /** Asked only after « oui » (P4). */
  later: boolean;
}

export function checklist(
  c: CallDraft,
  sale: SoldSale | null,
  /** The property's transfer instructions, when the arrhes are paid by transfer: undefined or null = none set. */
  bank?: Pick<BankTransferDetails, 'dueDays'> | null,
  today?: IsoDate,
): CheckItem[] {
  const rooms = allRooms(c, sale);
  const sold = rooms.every((r) => r.sale !== null);
  const kids = rooms.some((r) => r.childAges.length > 0);
  const items: CheckItem[] = [
    { label: 'Dates possibles', ok: c.arrival !== '' && sold, step: 'dates', later: false },
    ...(kids ? [{ label: 'Âge des enfants', ok: rooms.every((r) => !agesMissing(r)), step: 'dates' as const, later: false }] : []),
    { label: rooms.length > 1 ? `Chambre et formule (${rooms.length} chambres)` : 'Chambre et formule', ok: sold, step: 'offre', later: false },
    { label: 'Le client veut réserver', ok: c.decision === 'book', step: 'decision', later: false },
    { label: 'Nom', ok: c.lastName.trim() !== '', step: 'coord', later: true },
    { label: 'Prénom', ok: c.firstName.trim() !== '', step: 'coord', later: true },
    { label: 'Téléphone', ok: phoneOk(c.phone), step: 'coord', later: true },
    // The secure link (P2, P13) goes by e-mail: no address, no guarantee.
    { label: 'E-mail (confirmation et lien carte)', ok: emailOk(c.email), step: 'coord', later: true },
    ...(c.arrhes === 'transfer' ? transferChecks(c, rooms, bank, today) : []),
    { label: 'Récapitulatif confirmé', ok: c.confirmed, step: 'recap', later: true },
  ];
  return items;
}

/** What a transfer needs on top (PB10): the property's instructions, arrhes to receive, a due day in range. */
function transferChecks(c: CallDraft, rooms: readonly CallRoom[], bank: Pick<BankTransferDetails, 'dueDays'> | null | undefined, today?: IsoDate): CheckItem[] {
  const due = today === undefined ? null : transferDue(c, bank ?? null, today);
  return [
    { label: 'Coordonnées bancaires de l’établissement (Configuration › Conditions)', ok: bank != null, step: 'arrhes', later: true },
    { label: 'Arrhes à recevoir par virement (montant supérieur à 0)', ok: depositCents(c.deposit ?? DEFAULT_DEPOSIT, roomsTotal(rooms)) > 0, step: 'arrhes', later: true },
    {
      label: 'Échéance du virement (aujourd’hui → arrivée)',
      ok: due !== null && today !== undefined && c.arrival !== '' && transferDueProblem(due, today, c.arrival) === null,
      step: 'arrhes',
      later: true,
    },
  ];
}

export const canSave = (c: CallDraft, sale: SoldSale | null, bank?: Pick<BankTransferDetails, 'dueDays'> | null, today?: IsoDate) =>
  checklist(c, sale, bank, today).every((i) => i.ok);

// ── The manual command (PB4) ─────────────────────────────────────────────────

export interface CallCatalogue {
  roomTypes: readonly RoomType[];
  ratePlans: readonly RatePlan[];
  products: readonly Product[];
  /** The desk may write health data (`pms.needs.read`) — else needs go unsaid. */
  canNeeds: boolean;
  /** The property's cancellation policies (PB10) — what a picked `policyCode` stands for. */
  policies?: readonly StoredCancellationPolicy[];
  /** The property's bank-transfer instructions; null or absent = none set (a transfer cannot be sold). */
  bank?: BankTransferDetails | null;
  /** The Paris day of the call — the transfer's delay counts from it. */
  today?: IsoDate;
}

// ── The rooms behind the offer: bed configurations, the rooms free ───────────

/** What the desk can say of a room at a glance, read from the room's own facts.
 *  Shown on each free room, no longer asked as « préférences » (operator,
 *  2026-10-27: the desk picks the room itself). */
export const ROOM_FACTS: readonly { key: string; label: string; fits: (r: Room) => boolean }[] = [
  { key: 'jardin', label: 'côté jardin', fits: (r) => (r.view ?? '').trim().toLowerCase() === 'jardin' },
  { key: 'pmr', label: 'accessible (PMR)', fits: (r) => /\bPMR\b/i.test(r.remarks ?? '') },
];
export const roomFacts = (r: Room) => ROOM_FACTS.filter((f) => f.fits(r));

/** The house order of the categories (operator, 2026-10-27): the reference
 *  list comes sorted by code, which puts FAM before TSTD. Unlisted codes (the
 *  apartments) follow, by code. */
export const CATEGORY_ORDER: readonly string[] = ['DSTD', 'DSUP', 'TSTD', 'TSUP', 'FAM', 'FAMS'];
export function categoryRank(code: string): number {
  const i = CATEGORY_ORDER.indexOf(code);
  return i < 0 ? CATEGORY_ORDER.length : i;
}
export const byCategoryOrder = (a: string, b: string): number => categoryRank(a) - categoryRank(b) || a.localeCompare(b);

/** The formule as the desk writes it on the card: « PdJ », « DP », nothing
 *  for a room alone (operator, 2026-10-28). A channel's plan (BB-BK) reads as
 *  its base. */
const PLAN_SHORT: Record<string, string> = { BB: 'PdJ', DP: 'DP' };
export const planShort = (code: string): string => PLAN_SHORT[code.split('-')[0] ?? code] ?? '';

/** The rooms of a category in this établissement. */
export function categoryRooms(rooms: readonly Room[], roomTypes: readonly RoomType[], code: string, propertyCode: string): Room[] {
  const type = roomTypes.find((t) => t.code === code && t.propertyCode === propertyCode);
  return type ? rooms.filter((r) => r.roomTypeId === type.id) : [];
}

/** The beds a configuration sleeps; a configuration too small for the party is not offered. */
const sleeps = (b: BedConfig) => b.layout.doubles * 2 + b.layout.singles;

/** The configurations at least one of these rooms can be made up in, for this many people. */
export function bedChoices(rooms: readonly Room[], configs: readonly BedConfig[], people: number): BedConfig[] {
  const offered = new Set(rooms.flatMap((r) => r.configOptions));
  return configs.filter((b) => b.active && offered.has(b.code) && sleeps(b) >= people).sort((a, b) => a.sortOrder - b.sortOrder);
}

/** The rooms no live, assigned logement occupies on any night of [from, to). A
 *  logement not yet given a room is not counted here: the category's
 *  availability already holds it. */
export function freeRoomIds(rooms: readonly Room[], stays: readonly RoomStayView[], from: IsoDate, to: IsoDate): Set<number> {
  const taken = new Set(
    stays
      .filter((s) => s.roomId !== null && s.cancelledAt === null && s.status !== 'cancelled')
      .filter((s) => s.checkinDate < to && s.checkoutDate > from)
      .map((s) => s.roomId as number),
  );
  return new Set(rooms.filter((r) => !taken.has(r.id)).map((r) => r.id));
}

/** The rooms that can be made up in a bed configuration (any, when none is asked). */
export function roomsFitting(rooms: readonly Room[], bed: string | null): Room[] {
  return rooms.filter((r) => bed === null || r.configOptions.includes(bed));
}

export function persons(c: CallDraft, room: Party = c): StayPersonInput[] {
  const to = departureOf(c);
  if (to === null) return [];
  const at = { startDate: c.arrival as IsoDate, endDate: to };
  return [
    ...Array.from({ length: room.adults }, () => ({ name: null, ageBand: 'adult' as AgeBand, ...at })),
    ...room.childAges.map((a) => ({ name: null, ageBand: bandForAge(a), ...at })),
  ];
}

/** The booking note: what the desk heard that has no field of its own. */
export function bookingNote(c: CallDraft): string | null {
  // The bed configuration has its own field (`requestedBedConfig`), the room its own (`roomId`).
  const lines = [
    c.occasion ? `Occasion : ${c.occasion}` : '',
    c.overrideCents !== null ? 'Prix négocié' : '',
    c.note.trim(),
  ].filter(Boolean);
  return lines.length ? lines.join('\n') : null;
}

export type BuildResult = { ok: true; command: ReservationCommand } | { ok: false; reason: string };

export function buildCallCommand(c: CallDraft, sale: SoldSale | null, cat: CallCatalogue): BuildResult {
  if (!canSave(c, sale, cat.bank ?? null, cat.today) || sale === null) return { ok: false, reason: 'La fiche « Pour réserver » n’est pas complète.' };
  // PB10: a picked policy travels as its rules + its code, so the database takes its label and says
  // « assouplies » only if the desk changed a number. A pick that is no longer there is not guessed.
  const picked = pickedPolicy(c, cat.policies ?? []);
  if (c.policyCode !== null && picked === null) return { ok: false, reason: 'La politique choisie n’existe plus : choisissez-en une autre à l’étape Arrhes.' };
  const soldTerms: ReservationCommand['rooms'][number]['cancellationTerms'] =
    c.relaxed !== null
      ? { ...c.relaxed, ...(picked !== null ? { policyCode: picked.code } : {}) }
      : picked !== null
        ? { ...policyRules(picked), policyCode: picked.code }
        : undefined;
  const transfer = c.arrhes === 'transfer';
  const due = transfer && cat.today !== undefined ? transferDue(c, cat.bank ?? null, cat.today) : null;
  const typeId = (code: string) => cat.roomTypes.find((t) => t.code === code && t.propertyCode === c.propertyCode)?.id;
  const rooms = allRooms(c, sale);
  const occupant = c.occupantName.trim();
  const extras = Object.entries(c.extras)
    .filter(([, q]) => q > 0)
    .map(([id, q]) => ({ productId: Number(id), quantity: q }));
  const stays: ReservationCommand['rooms'] = [];
  for (const [i, room] of rooms.entries()) {
    const s = room.sale!;
    const host = typeId(s.host);
    const sold = typeId(s.category);
    const plan = cat.ratePlans.find((p) => p.code === s.plan && p.propertyCode === c.propertyCode)?.id;
    if (host === undefined || sold === undefined || plan === undefined) {
      return { ok: false, reason: 'Catégorie ou plan introuvable dans cet établissement.' };
    }
    const nights = nightPrices(s, room.overrideCents);
    const people = persons(c, room);
    // The booking's own facts — needs, occupant, extras — ride on the first room.
    if (i === 0 && c.needs.trim() && cat.canNeeds && people[0]) people[0] = { ...people[0], needs: c.needs.trim() };
    stays.push({
      roomTypeId: host,
      // P6: the category bought and priced; the host above is what availability counts.
      soldRoomTypeId: sold,
      ratePlanId: plan,
      // The room given at the call is assigned after the save, so the journal dates it.
      roomId: null,
      checkin: s.quote.from,
      checkout: s.quote.to,
      adults: room.adults,
      children: people.filter((p) => p.ageBand !== 'adult' && p.ageBand !== 'infant').length,
      infants: people.filter((p) => p.ageBand === 'infant').length,
      occupantName: i === 0 && occupant !== '' ? occupant : null,
      priceCents: nights[0]?.priceCents ?? 0,
      nights,
      persons: people,
      products: i === 0 ? extras : [],
      ...(soldTerms !== undefined ? { cancellationTerms: soldTerms } : {}),
      ...(room.bed !== null ? { requestedBedConfig: room.bed } : {}),
    });
  }
  const deposit = depositCents(c.deposit ?? DEFAULT_DEPOSIT, roomsTotal(rooms));

  return {
    ok: true,
    command: {
      envelope: { source: 'manual', mode: 'create', reservationId: null, externalRef: null, revision: null },
      guest: {
        id: c.guestId,
        draft: {
          lastName: c.lastName.trim().toUpperCase(),
          firstName: c.firstName.trim(),
          email: c.email.trim(),
          phone: c.phone.trim(),
          lang: c.lang,
          notes: null,
        },
      },
      booking: {
        status: 'confirmed',
        notes: bookingNote(c),
        // P2: the secure link, or (PB10) a bank transfer; the arrhes are the desk's, 0 included for a link (P3).
        guarantee: transfer ? 'transfer_pending' : 'vad_pending',
        vadAmountCents: deposit,
        ...(transfer && due !== null ? { transferDueOn: due } : {}),
        ...(c.arrivalFrom ? { arrivalFrom: c.arrivalFrom } : {}),
        ...(c.arrivalTo ? { arrivalTo: c.arrivalTo } : {}),
      },
      rooms: stays,
    },
  };
}

/** The room nights of every room, negotiated prices included. */
export const roomsTotal = (rooms: readonly CallRoom[]): Cents =>
  rooms.reduce((t, r) => t + (r.sale ? stayTotal(r.sale, r.overrideCents) : 0), 0);

/** The taxe de séjour of every room. */
export const roomsTax = (rooms: readonly CallRoom[]): Cents => rooms.reduce((t, r) => t + (r.sale?.quote.taxCents ?? 0), 0);

/**
 * The rooms given at the call, matched to the logements the save created: each
 * to a stay of its host category not yet taken, in order. What the screen
 * assigns once the booking exists (`assignRoomStay`), so the journal records
 * when — and by whom — each room was given.
 */
export function roomAssignments(
  rooms: readonly CallRoom[],
  stays: readonly Pick<RoomStayView, 'id' | 'roomTypeId'>[],
  hostTypeId: (code: string) => number | undefined,
): { stayId: RoomStayView['id']; roomId: number }[] {
  const left = [...stays];
  const out: { stayId: RoomStayView['id']; roomId: number }[] = [];
  for (const r of rooms) {
    const type = r.sale ? hostTypeId(r.sale.host) : undefined;
    const at = left.findIndex((s) => s.roomTypeId === type);
    if (at < 0) continue;
    const [stay] = left.splice(at, 1);
    if (r.roomId !== null) out.push({ stayId: stay!.id, roomId: r.roomId });
  }
  return out;
}

/** The reference read out to the guest — the direct door's: the id's first 8 characters. */
export const bookingRef = (reservationId: string) => reservationId.slice(0, 8).toUpperCase();

// ── An enquiry (PB9) ─────────────────────────────────────────────────────────

/**
 * The anonymous count of a call that ends without a booking. The offer given is
 * the option kept, or — when the desk read the options without keeping one — the
 * cheapest it could sell (what the caller heard first). Nothing sellable: a
 * denial. null when the dates were never asked: there is nothing to count.
 */
export function buildTurnaway(c: CallDraft, options: readonly DeskSale[], cat: CallCatalogue, overrideCents: Cents | null): TurnawayInput | null {
  if (c.arrival === '') return null;
  const sellable = options.filter((o): o is SoldSale => o.sellable);
  const kept = c.pick ? sellable.find((o) => o.category === c.pick!.category && o.plan === c.pick!.plan) : undefined;
  const given = kept ?? [...sellable].sort((a, b) => a.quote.totalCents - b.quote.totalCents)[0];
  const typeId = given ? cat.roomTypes.find((t) => t.code === given.category && t.propertyCode === c.propertyCode)?.id : undefined;
  const planId = given ? cat.ratePlans.find((p) => p.code === given.plan && p.propertyCode === c.propertyCode)?.id : undefined;
  return {
    propertyCode: c.propertyCode,
    channel: 'phone',
    arrival: c.arrival as IsoDate,
    nights: c.nights,
    persons: [...Array.from({ length: c.adults }, () => 'adult' as AgeBand), ...c.childAges.map(bandForAge)],
    quoted:
      given && typeId !== undefined && planId !== undefined
        ? { roomTypeId: typeId, ratePlanId: planId, totalCents: kept ? stayTotal(given, overrideCents) : given.quote.totalCents }
        : null,
  };
}

// ── The read-back and the confirmation ───────────────────────────────────────

/** Read aloud: « 215,40 euros » — the screen shows no currency sign (operator, 2026-10-27). */
const eurSaid = (cents: Cents) => `${fmtAmount(cents)} euros`;
const eurSaidEn = (cents: Cents) => `${(cents / 100).toFixed(2)} euros`;
/** Written to the guest: the e-mail keeps the sign — the reader may not be in euros. */
const eur = fmtEuros;
const eurEn = (cents: Cents) => `€${(cents / 100).toFixed(2)}`;

export interface CallFigures {
  categoryLabel: string;
  planLabel: string;
  lodged: boolean;
  stayCents: Cents;
  extrasCents: Cents;
  taxCents: Cents;
  depositCents: Cents;
  terms: TermsRules | null;
  today: IsoDate;
  /** Arrhes by bank transfer (PB10): the property's instructions and the day it is due. Absent or null = by the card link. */
  transfer?: { details: Pick<BankTransferDetails, 'accountHolder' | 'iban' | 'bic'>; dueOn: IsoDate } | null;
  /** A booking of several rooms: one line each. Absent or one: the single-room wording. */
  rooms?: RoomFigure[];
}

export interface RoomFigure {
  categoryLabel: string;
  planLabel: string;
  lodged: boolean;
  party: Party;
  stayCents: Cents;
}

const several = (f: CallFigures): RoomFigure[] | null => (f.rooms && f.rooms.length > 1 ? f.rooms : null);
const roomLineFr = (r: RoomFigure, i: number, money: (c: Cents) => string) =>
  `Chambre ${i + 1} : ${r.categoryLabel} — ${r.planLabel.toLowerCase()}, ${partyFr(r.party)}, ${money(r.stayCents)}${r.lodged ? ' (logée plus grand, sans supplément)' : ''}`;
const roomLineEn = (r: RoomFigure, i: number, money: (c: Cents) => string) =>
  `Room ${i + 1}: ${r.categoryLabel}, ${r.planLabel.toLowerCase()}, ${partyEn(r.party)}, ${money(r.stayCents)}${r.lodged ? ' (a larger room at no extra charge)' : ''}`;

/** The terms read aloud: « 64,62 euros », not the sign. */
const saidEuros = (line: string) => line.replace(/\s?€/g, ' euros');
function spoken<T extends { cancellation: string[]; noShow: string }>(r: T): T {
  return { ...r, cancellation: r.cancellation.map(saidEuros), noShow: saidEuros(r.noShow) };
}

export function recapText(c: CallDraft, f: CallFigures): string {
  const to = departureOf(c);
  const name = `${c.firstName.trim()} ${c.lastName.trim().toUpperCase()}`.trim() || '[nom]';
  const email = c.email.trim() || '[e-mail]';
  const rendered = f.terms && c.arrival ? spoken(renderTerms(f.terms, c.lang === 'fr' ? 'fr' : 'en', { arrival: c.arrival as IsoDate, totalCents: f.stayCents, bookedOn: f.today })) : null;
  const multi = several(f);
  if (c.lang === 'fr') {
    return [
      ...(multi
        ? [
            `Je récapitule : une réservation au nom de ${name}, ${datesReadback(c)}, pour ${multi.length} chambres :`,
            ...multi.map((r, i) => `— ${roomLineFr(r, i, eurSaid)}`),
          ]
        : [
            `Je récapitule : une réservation au nom de ${name}, pour ${partyFr(c)}, ${datesReadback(c)}, en ${f.categoryLabel} — ${f.planLabel.toLowerCase()}.` +
              (f.lodged ? ' Nous vous installerons dans une chambre plus spacieuse, sans supplément.' : ''),
          ]),
      `Le séjour est à ${eurSaid(f.stayCents)}${c.overrideCents !== null ? ' (prix convenu)' : ''}${f.extrasCents ? `, plus ${eurSaid(f.extrasCents)} d’extras` : ''}, et la taxe de séjour de ${eurSaid(f.taxCents)} s’ajoute.`,
      f.transfer
        ? `Pour confirmer, vous recevrez à l’adresse ${email} nos coordonnées bancaires : des arrhes de ${eurSaid(f.depositCents)} sont à virer avant le ${termsDate(f.transfer.dueOn, 'fr')} inclus.`
        : f.depositCents > 0
          ? `Pour confirmer, vous recevrez à l’adresse ${email} un lien sécurisé où saisir votre carte ; nous y prélèverons des arrhes de ${eurSaid(f.depositCents)}.`
          : `Pour la garantir, vous recevrez à l’adresse ${email} un lien sécurisé ; rien n’est prélevé aujourd’hui.`,
      ...(rendered ? [`Conditions d’annulation : ${rendered.cancellation.join(' ')} ${rendered.noShow}`] : []),
      ...(c.arrivalFrom ? [`Vous pensez arriver vers ${c.arrivalFrom.replace(':', ' h ')}.`] : []),
      'Est-ce bien correct ?',
    ].join('\n');
  }
  const dates = `from ${c.arrival ? longEn(c.arrival as IsoDate) : '…'} to ${to ? longEn(to) : '…'} — ${plural(c.nights, 'night', 'nights')}`;
  return [
    ...(multi
      ? [`Let me read that back: a booking under the name of ${name}, ${dates}, for ${multi.length} rooms:`, ...multi.map((r, i) => `— ${roomLineEn(r, i, eurSaidEn)}`)]
      : [
          `Let me read that back: a booking under the name of ${name}, for ${partyEn(c)}, ${dates} — in ${f.categoryLabel}, ${f.planLabel.toLowerCase()}.` +
            (f.lodged ? ' We will give you a larger room at no extra charge.' : ''),
        ]),
    `The stay comes to ${eurSaidEn(f.stayCents)}${c.overrideCents !== null ? ' (agreed price)' : ''}${f.extrasCents ? `, plus ${eurSaidEn(f.extrasCents)} of extras` : ''}, and the city tax of ${eurSaidEn(f.taxCents)} is added.`,
    f.transfer
      ? `To confirm it, you will receive our bank details at ${email}: a deposit (arrhes) of ${eurSaidEn(f.depositCents)} is to be transferred by ${termsDate(f.transfer.dueOn, 'en')} inclusive.`
      : f.depositCents > 0
        ? `To confirm it, you will receive a secure link at ${email} to enter your card; we will charge a deposit of ${eurSaidEn(f.depositCents)} to it.`
        : `To guarantee it, you will receive a secure link at ${email}; nothing is charged today.`,
    ...(rendered ? [`Cancellation terms: ${rendered.cancellation.join(' ')} ${rendered.noShow}`] : []),
    ...(c.arrivalFrom ? [`You expect to arrive around ${c.arrivalFrom}.`] : []),
    'Is everything correct?',
  ].join('\n');
}

/** The confirmation the desk copies into its mailbox — the platform sends no e-mail (§2). */
export function confirmationText(c: CallDraft, f: CallFigures, ref: string): string {
  const to = departureOf(c);
  const fr = c.lang === 'fr';
  const rendered = f.terms && c.arrival ? renderTerms(f.terms, fr ? 'fr' : 'en', { arrival: c.arrival as IsoDate, totalCents: f.stayCents, bookedOn: f.today }) : null;
  const multi = several(f);
  if (fr) {
    return [
      `Objet : Confirmation de votre réservation ${ref}`,
      '',
      `Bonjour ${c.firstName.trim()} ${c.lastName.trim().toUpperCase()},`,
      '',
      'Nous avons le plaisir de confirmer votre réservation :',
      `• Arrivée le ${c.arrival ? longFr(c.arrival as IsoDate) : '…'}, départ le ${to ? longFr(to) : '…'} (${plural(c.nights, 'nuit', 'nuits')})`,
      ...(multi ? multi.map((r, i) => `• ${roomLineFr(r, i, eur)}`) : [`• ${f.categoryLabel} — ${f.planLabel.toLowerCase()}, ${partyFr(c)}`]),
      `• Séjour : ${eur(f.stayCents)}${f.extrasCents ? ` · extras : ${eur(f.extrasCents)}` : ''} · taxe de séjour : ${eur(f.taxCents)}`,
      f.transfer
        ? `• Arrhes : ${eur(f.depositCents)}, à régler par virement (coordonnées bancaires ci-dessous)`
        : f.depositCents > 0
          ? `• Arrhes : ${eur(f.depositCents)}, prélevées sur la carte saisie sur le lien sécurisé [LIEN]`
          : '• Garantie : votre carte, saisie sur le lien sécurisé [LIEN] ; rien n’est prélevé à la réservation',
      ...(c.arrivalFrom ? [`• Arrivée prévue vers ${c.arrivalFrom}`] : []),
      ...(f.transfer
        ? ['', ...transferInstructionLines({ details: f.transfer.details, amountCents: f.depositCents, dueOn: f.transfer.dueOn, reference: ref }, 'fr')]
        : []),
      ...(rendered ? ['', 'Conditions d’annulation :', ...rendered.cancellation.map((l) => `• ${l}`), `• ${rendered.noShow}`] : []),
      '',
      'Au plaisir de vous accueillir,',
      'La réception',
    ].join('\n');
  }
  return [
    `Subject: Your booking ${ref} is confirmed`,
    '',
    `Dear ${c.firstName.trim()} ${c.lastName.trim().toUpperCase()},`,
    '',
    'We are pleased to confirm your booking:',
    `• From ${c.arrival ? longEn(c.arrival as IsoDate) : '…'} to ${to ? longEn(to) : '…'} (${plural(c.nights, 'night', 'nights')})`,
    ...(multi ? multi.map((r, i) => `• ${roomLineEn(r, i, eurEn)}`) : [`• ${f.categoryLabel}, ${f.planLabel.toLowerCase()}, ${partyEn(c)}`]),
    `• Stay: ${eurEn(f.stayCents)}${f.extrasCents ? ` · extras: ${eurEn(f.extrasCents)}` : ''} · city tax: ${eurEn(f.taxCents)}`,
    f.transfer
      ? `• Deposit (arrhes): ${eurEn(f.depositCents)}, to be paid by bank transfer (details below)`
      : f.depositCents > 0
        ? `• Deposit: ${eurEn(f.depositCents)}, charged to the card you enter on the secure link [LINK]`
        : '• Guarantee: your card, entered on the secure link [LINK] — nothing is charged at booking',
    ...(c.arrivalFrom ? [`• Expected arrival around ${c.arrivalFrom}`] : []),
    ...(f.transfer
      ? ['', ...transferInstructionLines({ details: f.transfer.details, amountCents: f.depositCents, dueOn: f.transfer.dueOn, reference: ref }, 'en')]
      : []),
    ...(rendered ? ['', 'Cancellation terms:', ...rendered.cancellation.map((l) => `• ${l}`), `• ${rendered.noShow}`] : []),
    '',
    'We look forward to welcoming you,',
    'Reception',
  ].join('\n');
}

/**
 * The confirmation as the platform sends it (M2–M3): the same text as
 * `confirmationText`, split into subject and body, the card link's mark where
 * the desk wrote [LIEN] (the database puts the link in at send time), signed by
 * the person identified (« Marie, réception »). Sent in the text's own
 * language: French, else English.
 */
export function confirmationMail(
  c: CallDraft,
  f: CallFigures,
  ref: string,
  signer: string | null,
): { subject: string; body: string; lang: ConfirmationMailLang } {
  const fr = c.lang === 'fr';
  const [first = '', , ...rest] = confirmationText(c, f, ref).split('\n');
  let body = rest.join('\n').replace(fr ? '[LIEN]' : '[LINK]', CARD_LINK_MARK);
  const who = signer?.trim();
  if (who) body = body.replace(fr ? /\nLa réception$/ : /\nReception$/, fr ? `\n${who}, réception` : `\n${who}, reception`);
  return { subject: first.replace(/^(Objet|Subject) ?: ?/, ''), body, lang: fr ? 'fr' : 'en' };
}
